import 'signer-app/screenreaders.module.css';

import React from 'react';
import { IntlShape, injectIntl } from 'react-intl';
import { FileRejection } from 'react-dropzone';

import * as signatureTypes from 'signer-app/signature-modal/constants/signature-types';
import SignatureEditArea from 'signer-app/signature-modal/upload/signature-edit-area';
import { Bounds } from 'signer-app/signature-modal/upload/croppable';
import { SignatureModalContext } from 'signer-app/signature-modal/signature-modal-context/context';
import { CONTRAST_MID_VALUE } from './upload-constants';
import { validateUploadFile } from './validate-upload-file';
import { SignatureUploadDropzone } from './signature-upload-dropzone';

export { MAX_FILE_SIZE } from './upload-constants';

export interface UploadProps {
  uploadedSignature: SignatureModalContext['uploadedSignature'];
  isUploadingSignature: boolean;
  intl: IntlShape;
  onRotateSignature: SignatureModalContext['onRotateSignature'];
  clearUploadedSignature: () => void;
  getSignatureUrl: SignatureModalContext['getSignatureUrl'];
  uploadSignature: (file: File) => void;
  enableInsertButtonCallback: (enable: boolean) => void;
  isMobile: boolean;
  isNewSignerApp?: boolean;
}

interface UploadState {
  showFileSizeError: boolean;
  showFileFormatError: boolean;
  isUploadingSignature: boolean;
  uploadProgress: number;
  contrast: number;
  rotation: number;
  bounds: Bounds | null;
}

class Upload extends React.PureComponent<UploadProps, UploadState> {
  uploadButtonRef = React.createRef<HTMLButtonElement>();

  state: UploadState = {
    showFileSizeError: false,
    showFileFormatError: false,
    isUploadingSignature: false,
    uploadProgress: 0,
    contrast: CONTRAST_MID_VALUE,
    rotation: 0,
    bounds: null,
  };

  updateProgressInterval: NodeJS.Timer | null = null;

  onDropAccepted = ([file]: File[]) => {
    this.upload(file);

    // Clear errors
    this.setState({
      showFileSizeError: false,
      showFileFormatError: false,
    });
  };

  onDropRejected = ([{ file }]: FileRejection[]) => {
    const { showFileSizeError, showFileFormatError } = validateUploadFile(file);

    this.setState({
      showFileSizeError,
      showFileFormatError,
    });
  };

  upload(file: File) {
    this.setState(
      {
        isUploadingSignature: true,
      },
      () => {
        this.props.uploadSignature(file);
      },
    );
  }

  componentDidMount() {
    this.props.enableInsertButtonCallback(this.shouldEnableInsert());
  }

  componentDidUpdate(prevProps: UploadProps) {
    this.props.enableInsertButtonCallback(this.shouldEnableInsert());

    // We don't have any visibility into this, so we'll
    // have to make some assumptions based on the props
    // passed to this component.
    if (!prevProps.uploadedSignature && this.props.uploadedSignature) {
      this.setState({
        isUploadingSignature: false,
      });
    }

    if (prevProps.uploadedSignature && !this.props.uploadedSignature) {
      // prevent focus from leaving the upload component on delete
      // when signature gets deleted then focus back on the upload button
      this.uploadButtonRef.current?.focus();
    }
  }

  getSignatureData() {
    const signature = this.props.uploadedSignature;
    const { contrast, rotation, bounds } = this.state;

    if (signature === null) {
      return {};
    }

    const signatureData: Record<string, any> = {
      create_type_code: signatureTypes.UPLOAD,
      guid: signature.guid,
      threshold: contrast,
      degrees: rotation,
    };

    if (bounds) {
      signatureData.is_cropped = true;
      signatureData.x = bounds.left;
      signatureData.y = bounds.top;
      signatureData.width = bounds.width;
      signatureData.height = bounds.height;
    } else {
      signatureData.width = signature.width;
      signatureData.height = signature.height;
    }

    return signatureData;
  }

  shouldEnableInsert() {
    return !!this.props.uploadedSignature;
  }

  handleClearUploadedSignature = () => {
    this.props.clearUploadedSignature();
    this.setState({
      contrast: CONTRAST_MID_VALUE,
      rotation: 0,
    });
  };

  handlePreviewChange = (
    changes: Partial<{
      bounds: Bounds | null;
      rotation: number;
      contrast: number;
    }>,
  ) => {
    const { uploadedSignature, onRotateSignature } = this.props;

    if (changes.rotation && uploadedSignature !== null) {
      onRotateSignature(uploadedSignature);
    }

    const {
      bounds = this.state.bounds,
      rotation = this.state.rotation,
      contrast = this.state.contrast,
    } = changes;

    this.setState({
      bounds,
      rotation,
      contrast,
    });
  };

  renderDropzone() {
    const { isUploadingSignature, showFileFormatError, showFileSizeError } =
      this.state;

    const { isNewSignerApp = false } = this.props;

    return (
      <SignatureUploadDropzone
        isUploading={isUploadingSignature}
        showFileSizeError={showFileSizeError}
        showFileFormatError={showFileFormatError}
        isNewSignerApp={isNewSignerApp}
        uploadButtonRef={this.uploadButtonRef}
        onDropAccepted={this.onDropAccepted}
        onDropRejected={this.onDropRejected}
      />
    );
  }

  render() {
    const { getSignatureUrl, uploadedSignature, isMobile } = this.props;
    const hasUploadedSignature = uploadedSignature !== null;
    const { rotation, contrast, isUploadingSignature } = this.state;

    return (
      <div aria-busy={isUploadingSignature}>
        {!isUploadingSignature && hasUploadedSignature ? (
          <SignatureEditArea
            getSignatureUrl={getSignatureUrl}
            signature={uploadedSignature}
            clearUploadedSignature={this.handleClearUploadedSignature}
            onPreviewChange={this.handlePreviewChange}
            contrast={contrast}
            rotation={rotation}
            isMobile={isMobile}
          />
        ) : (
          this.renderDropzone()
        )}
      </div>
    );
  }
}

export default injectIntl(Upload, { forwardRef: true });
